/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ApiKeyInfoBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Application Keys module.
----------------------------------------------------------------*/


import { Key, AlertCircle } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import TenantSelectSimple from '../../../components/ui/TenantSelectSimple';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';

const INPUT_CLS = 'px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent';
const SELECT_CLS = 'px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed';
const TTL_PROPS = { position: 'top' as const, maxWidth: '300px' };

interface Domain { id: number; name: string; }
interface ApiKeyTenant { id: number | string; name: string; }

interface ApiKeyFormData {
    name: string;
    description: string;
    tenantId: string | number | null;
    domainId: number | null;
    expiresAt: string;
    isActive: boolean;
}

interface Props {
    formData: ApiKeyFormData;
    errors: Record<string, string>;
    tenants: any[];
    loadingTenants: boolean;
    domains: Domain[];
    loadingDomains: boolean;
    onInputChange: (field: string, value: unknown) => void;
    onLoadDomains: (tenantId: number) => void;
}

export function ApiKeyInfoBox({ formData, errors, tenants, loadingTenants, domains, loadingDomains, onInputChange, onLoadDomains }: Props) {
    const { t } = useI18n();
    const { isActive: debugMode } = useDebugMode();
    const dbgLabel = (label: string, field: string) => (
        <div className="flex flex-col">
            <label className="text-sm font-medium text-foreground">{label}</label>
            {debugMode && <span className="text-xs text-muted-foreground mt-0.5">{field}</span>}
        </div>
    );

    return (
        <FormBox title={t('apiKeys.sections.basicInformation')} icon={<Key className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                {/* Name */}
                <SimpleTooltip content={t('apiKeys.tooltips.apiKeyName')} {...TTL_PROPS}>{dbgLabel(`${t('apiKeys.apiKeyName')} *`, 'name')}</SimpleTooltip>
                <input id="apiKeyName" type="text" value={formData.name} onChange={(e) => onInputChange('name', e.target.value)} className={`${INPUT_CLS} ${errors.name ? 'border-red-500' : 'border-border'}`} placeholder={t('apiKeys.enterApiKeyName')} />

                {/* Description */}
                <SimpleTooltip content={t('apiKeys.tooltips.description')} {...TTL_PROPS}>{dbgLabel(t('apiKeys.description'), 'description')}</SimpleTooltip>
                <input id="apiKeyDescription" type="text" value={formData.description} onChange={(e) => onInputChange('description', e.target.value)} className={`${INPUT_CLS} border-border`} placeholder={t('apiKeys.enterDescription')} />

                {/* Tenant */}
                <SimpleTooltip content={t('apiKeys.tooltips.tenant')} {...TTL_PROPS}>{dbgLabel(t('apiKeys.tenant'), 'tenant_id')}</SimpleTooltip>
                <TenantSelectSimple
                    value={formData.tenantId || null}
                    onChange={(value) => {
                        onInputChange('tenantId', value === null ? null : value);
                        onInputChange('domainId', null);
                        if (value !== null) onLoadDomains(Number(value));
                    }}
                    tenants={tenants}
                    loading={loadingTenants}
                    disabled={loadingTenants}
                    showGlobalAccess
                    globalAccessLabel={t('apiKeys.globalAccess')}
                    searchPlaceholder={t('common.search')}
                    noResultsText={t('common.noResults')}
                    noDataText={t('common.noData')}
                />

                {/* Domain Scope */}
                <SimpleTooltip content={t('apiKeys.tooltips.domainScope')} {...TTL_PROPS}>{dbgLabel(t('apiKeys.domainScope'), 'domain_id')}</SimpleTooltip>
                <select id="apiKeyDomain" value={formData.domainId ?? ''} onChange={(e) => onInputChange('domainId', e.target.value === '' ? null : Number(e.target.value))} className={SELECT_CLS} disabled={!formData.tenantId || loadingDomains}>
                    <option value="">{t('apiKeys.allDomains')}</option>
                    {domains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>

                {/* Expires At */}
                <SimpleTooltip content={t('apiKeys.tooltips.expiresAt')} {...TTL_PROPS}>{dbgLabel(t('apiKeys.expiresAt'), 'expires_at')}</SimpleTooltip>
                <div>
                    <input id="apiKeyExpiresAt" type="date" value={formData.expiresAt} onChange={(e) => onInputChange('expiresAt', e.target.value)} className={`${INPUT_CLS} ${errors.expiresAt ? 'border-red-500' : 'border-border'}`} min={new Date().toISOString().split('T')[0]} />
                    {!formData.expiresAt && <p className="mt-1 text-xs text-muted-foreground">{t('apiKeys.noExpirationSet')}</p>}
                </div>

                {/* Active */}
                <SimpleTooltip content={t('apiKeys.tooltips.active')} {...TTL_PROPS}>{dbgLabel(t('apiKeys.active'), 'is_active')}</SimpleTooltip>
                <div className="flex justify-start">
                    <Toggle id="apiKeyActive" value={formData.isActive} onChange={(v) => onInputChange('isActive', v)} />
                </div>
                <div /><div />
            </div>

            {errors.name && <p className="mt-2 text-xs text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.name}</p>}
            {errors.expiresAt && <p className="mt-2 text-xs text-red-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.expiresAt}</p>}
        </FormBox>
    );
}
